import { createFileRoute, redirect } from '@tanstack/react-router';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  hiringRulesContentSchema,
  hiringAntiCheatConfigSchema,
  type HiringRulesContentInput,
  type HiringAntiCheatConfigInput,
} from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { TextField, TextAreaField } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';

/**
 * System-level editor for the hiring content the academy owner controls
 * without a deploy. Three KV-backed sections live here:
 *
 * 1. Onboarding config (probation length + salary tiers) — what selected
 *    candidates see on `/candidate/rules` and `/candidate/onboard`. Stored
 *    at `hiring:onboarding-config`. super_admin only.
 * 2. Rules content (L-3) — the academy "house rules" list shown above the
 *    rules test on `/candidate/rules`. Stored at `hiring:rules-content`.
 *    super_admin only. Add / remove / reorder / edit rule sections.
 * 3. Anti-cheat thresholds (L-2) — the tab-switch flag / void thresholds
 *    for `hiring_strict` aptitude attempts, plus the "email HR on flag"
 *    toggle. Stored at `hiring:anti-cheat-config`. super_admin or hr.
 *
 * Role model: the route admits super_admin (full access) and hr. An hr
 * user sees only the anti-cheat section because that is the only one whose
 * write endpoint admits hr — the onboarding / rules PATCHes are
 * super_admin-only, so rendering them for hr would always 403 on save.
 */

const isSuperAdmin = (): boolean => {
  const user = useAuthStore.getState().user;
  return user?.primaryRole === 'super_admin' || !!user?.roles.includes('super_admin');
};

const isHrOrAdmin = (): boolean => {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  return (
    user.primaryRole === 'super_admin' ||
    user.primaryRole === 'hr' ||
    user.roles.some((r) => r === 'super_admin' || r === 'hr')
  );
};

export const Route = createFileRoute('/_authed/system/hiring-config')({
  beforeLoad: () => {
    // beforeLoad runs outside React, so read the store directly. Admit
    // super_admin (full access) and hr (anti-cheat only). Anyone else is
    // bounced to the landing route, matching the other system pages.
    const user = useAuthStore.getState().user;
    if (!user) return;
    const allowed =
      user.primaryRole === 'super_admin' ||
      user.primaryRole === 'hr' ||
      user.roles.some((r) => r === 'super_admin' || r === 'hr');
    if (!allowed) {
      throw redirect({ to: '/' });
    }
  },
  component: HiringConfigPage,
});

function HiringConfigPage() {
  const superAdmin = isSuperAdmin();
  const hrOrAdmin = isHrOrAdmin();

  return (
    <ListShell
      title="Hiring settings"
      description="Academy-owned hiring content the owner can edit without a deploy: onboarding terms, the candidate rules list, and aptitude anti-cheat thresholds."
    >
      <div className="space-y-10">
        {superAdmin ? <OnboardingConfigSection /> : null}
        {superAdmin ? <RulesContentSection /> : null}
        {hrOrAdmin ? <AntiCheatSection /> : null}
      </div>
    </ListShell>
  );
}

/* ======================================================================
 * Section shell — consistent heading + description for each card.
 * ==================================================================== */

function SectionShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <p className="text-sm text-[color:var(--color-text-muted)]">
          {description}
        </p>
      </header>
      {children}
    </section>
  );
}

/* ======================================================================
 * 1. Onboarding config (probation + salary) — super_admin
 * ==================================================================== */

interface OnboardingConfig {
  probationWeeks: number | null;
  probationSalaryPaise: number | null;
  realSalaryPaise: number | null;
  note: string | null;
}

interface OnboardingConfigResponse {
  config: OnboardingConfig;
}

/**
 * The form works in rupees for UX (Indian academy, the owner thinks
 * in rupees, not paise). We multiply by 100 at submit time. Empty
 * string means "not set" — submitted as `null` so the candidate UI
 * shows the "discussed at interview" fallback for that field.
 */
const onboardingFormSchema = z
  .object({
    probationWeeks: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d+$/.test(v), 'Whole weeks only')
      .refine(
        (v) => v === '' || (Number(v) >= 1 && Number(v) <= 52),
        'Between 1 and 52 weeks',
      ),
    probationSalaryRupees: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d+$/.test(v), 'Whole rupees only')
      .refine(
        (v) => v === '' || Number(v) <= 10_000_000,
        'Up to ₹1,00,00,000',
      ),
    realSalaryRupees: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d+$/.test(v), 'Whole rupees only')
      .refine(
        (v) => v === '' || Number(v) <= 10_000_000,
        'Up to ₹1,00,00,000',
      ),
    note: z.string().max(2000),
  })
  .strict();

type OnboardingFormValues = z.infer<typeof onboardingFormSchema>;

const emptyOnboardingForm: OnboardingFormValues = {
  probationWeeks: '',
  probationSalaryRupees: '',
  realSalaryRupees: '',
  note: '',
};

const onboardingToForm = (c: OnboardingConfig): OnboardingFormValues => ({
  probationWeeks: c.probationWeeks === null ? '' : String(c.probationWeeks),
  probationSalaryRupees:
    c.probationSalaryPaise === null
      ? ''
      : String(Math.round(c.probationSalaryPaise / 100)),
  realSalaryRupees:
    c.realSalaryPaise === null
      ? ''
      : String(Math.round(c.realSalaryPaise / 100)),
  note: c.note ?? '',
});

const formToOnboarding = (v: OnboardingFormValues): OnboardingConfig => ({
  probationWeeks: v.probationWeeks === '' ? null : Number(v.probationWeeks),
  probationSalaryPaise:
    v.probationSalaryRupees === '' ? null : Number(v.probationSalaryRupees) * 100,
  realSalaryPaise:
    v.realSalaryRupees === '' ? null : Number(v.realSalaryRupees) * 100,
  note: v.note.trim() === '' ? null : v.note.trim(),
});

function OnboardingConfigSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['hiring', 'onboarding-config'] as const,
    queryFn: ({ signal }) =>
      apiRequest<OnboardingConfigResponse>('/api/hiring/onboarding-config', {
        signal,
      }),
  });

  return (
    <SectionShell
      title="Onboarding terms"
      description="What selected candidates see on their rules and onboarding pages. Leave any field blank to show 'discussed at interview' instead."
    >
      <GatedSection
        error={query.error}
        title="Hiring onboarding"
        onRetry={() => query.refetch()}
      >
        {query.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <OnboardingEditor
            initial={query.data?.config ?? null}
            onSaved={() => {
              toast('Onboarding terms updated.', { variant: 'success' });
              void queryClient.invalidateQueries({
                queryKey: ['hiring', 'onboarding-config'],
              });
            }}
          />
        )}
      </GatedSection>
    </SectionShell>
  );
}

function OnboardingEditor({
  initial,
  onSaved,
}: {
  initial: OnboardingConfig | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingFormSchema),
    defaultValues: initial ? onboardingToForm(initial) : emptyOnboardingForm,
  });

  const mutation = useMutation({
    mutationFn: (config: OnboardingConfig) =>
      apiRequest<OnboardingConfigResponse>('/api/hiring/onboarding-config', {
        method: 'PATCH',
        body: config,
      }),
    onSuccess: () => onSaved(),
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not save.', {
        variant: 'error',
      }),
  });

  return (
    <form
      onSubmit={handleSubmit((v) => mutation.mutate(formToOnboarding(v)))}
      className="max-w-xl space-y-5"
    >
      <TextField
        label="Probation period (weeks)"
        hint="Typically 12 weeks. Leave blank to omit."
        inputMode="numeric"
        error={errors.probationWeeks?.message}
        {...register('probationWeeks')}
      />
      <TextField
        label="Probation stipend (₹ / month)"
        hint="Monthly stipend during the probation window. Leave blank to omit."
        inputMode="numeric"
        error={errors.probationSalaryRupees?.message}
        {...register('probationSalaryRupees')}
      />
      <TextField
        label="Salary after probation (₹ / month)"
        hint="What the teacher earns once probation passes. Leave blank to omit."
        inputMode="numeric"
        error={errors.realSalaryRupees?.message}
        {...register('realSalaryRupees')}
      />
      <TextAreaField
        label="Note"
        optional
        rows={3}
        hint="Shown below the salary block. Use to clarify any conditions."
        error={errors.note?.message}
        {...register('note')}
      />
      <div className="flex justify-end">
        <Button type="submit" loading={isSubmitting || mutation.isPending}>
          Save onboarding terms
        </Button>
      </div>
    </form>
  );
}

/* ======================================================================
 * 2. Rules content (L-3) — super_admin. Add / remove / reorder / edit.
 * ==================================================================== */

interface RulesContentResponse {
  content: HiringRulesContentInput;
}

function RulesContentSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['hiring', 'rules-content'] as const,
    queryFn: ({ signal }) =>
      apiRequest<RulesContentResponse>('/api/hiring/rules-content', { signal }),
  });

  return (
    <SectionShell
      title="Rules content"
      description="The house rules shown to selected candidates before the rules test on /candidate/rules. Add, edit, reorder, or remove rules — changes appear on the next candidate page load, no deploy needed."
    >
      <GatedSection
        error={query.error}
        title="Rules content"
        onRetry={() => query.refetch()}
      >
        {query.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : (
          <RulesContentEditor
            initial={query.data?.content ?? { sections: [], intro: null }}
            onSaved={() => {
              toast('Rules content updated.', { variant: 'success' });
              void queryClient.invalidateQueries({
                queryKey: ['hiring', 'rules-content'],
              });
            }}
          />
        )}
      </GatedSection>
    </SectionShell>
  );
}

// The form value type IS the backend input type. `intro` is `string | null`
// but the textarea only ever writes a string; the schema's `.nullable()`
// accepts an empty string, and `formToRulesContent` coerces blank intro and
// trims every field at submit time so the persisted payload is clean.
const rulesContentToForm = (
  c: HiringRulesContentInput,
): HiringRulesContentInput => ({
  intro: c.intro ?? '',
  sections: c.sections.map((s) => ({ id: s.id, title: s.title, body: s.body })),
});

const formToRulesContent = (
  v: HiringRulesContentInput,
): HiringRulesContentInput => {
  const intro = (v.intro ?? '').trim();
  return {
    intro: intro === '' ? null : intro,
    sections: v.sections.map((s) => ({
      id: s.id,
      title: s.title.trim(),
      body: s.body.trim(),
    })),
  };
};

function RulesContentEditor({
  initial,
  onSaved,
}: {
  initial: HiringRulesContentInput;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<HiringRulesContentInput>({
    resolver: zodResolver(hiringRulesContentSchema),
    defaultValues: rulesContentToForm(initial),
  });

  // `keyName: 'fieldKey'` is critical: useFieldArray's default keyName is
  // 'id', which would shadow our real section id in the `fields` array. We
  // register a hidden input for the actual UUID so it round-trips into the
  // PATCH body, and key rows on `field.fieldKey`.
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'sections',
    keyName: 'fieldKey',
  });

  const mutation = useMutation({
    mutationFn: (content: HiringRulesContentInput) =>
      apiRequest<RulesContentResponse>('/api/hiring/rules-content', {
        method: 'PATCH',
        body: content,
      }),
    onSuccess: () => onSaved(),
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not save.', {
        variant: 'error',
      }),
  });

  const sectionsError =
    typeof errors.sections?.message === 'string'
      ? errors.sections.message
      : errors.sections?.root?.message;

  return (
    <form
      onSubmit={handleSubmit((v) => mutation.mutate(formToRulesContent(v)))}
      className="max-w-2xl space-y-5"
    >
      <TextAreaField
        label="Intro paragraph"
        optional
        rows={2}
        hint="Optional text shown above the rules list. Leave blank to omit."
        error={errors.intro?.message}
        {...register('intro')}
      />

      <div className="space-y-4">
        {fields.length === 0 ? (
          <p className="rounded-md border border-dashed border-[color:var(--color-border)] p-4 text-sm text-[color:var(--color-text-muted)]">
            No rules yet. Add the academy's house rules below — they'll appear
            for candidates in this order.
          </p>
        ) : (
          fields.map((field, index) => (
            <fieldset
              key={field.fieldKey}
              className="space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
            >
              <div className="flex items-center justify-between">
                <legend className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Rule {index + 1}
                </legend>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={<ArrowUp size={14} aria-hidden="true" />}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                    aria-label={`Move rule ${index + 1} up`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={<ArrowDown size={14} aria-hidden="true" />}
                    disabled={index === fields.length - 1}
                    onClick={() => move(index, index + 1)}
                    aria-label={`Move rule ${index + 1} down`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={<Trash2 size={14} aria-hidden="true" />}
                    onClick={() => remove(index)}
                    aria-label={`Remove rule ${index + 1}`}
                  />
                </div>
              </div>

              {/* Hidden input keeps the real UUID in the form payload. */}
              <input type="hidden" {...register(`sections.${index}.id`)} />

              <TextField
                label="Title"
                error={errors.sections?.[index]?.title?.message}
                {...register(`sections.${index}.title`)}
              />
              <TextAreaField
                label="Body"
                rows={3}
                error={errors.sections?.[index]?.body?.message}
                {...register(`sections.${index}.body`)}
              />
            </fieldset>
          ))
        )}

        {sectionsError ? (
          <p role="alert" className="text-sm text-danger">
            {sectionsError}
          </p>
        ) : null}

        <Button
          type="button"
          variant="secondary"
          icon={<Plus size={16} aria-hidden="true" />}
          disabled={fields.length >= 20}
          onClick={() =>
            append({ id: crypto.randomUUID(), title: '', body: '' })
          }
        >
          Add rule
        </Button>
        {fields.length >= 20 ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Maximum of 20 rules reached.
          </p>
        ) : null}
      </div>

      <div className="flex justify-end">
        <Button type="submit" loading={isSubmitting || mutation.isPending}>
          Save rules content
        </Button>
      </div>
    </form>
  );
}

/* ======================================================================
 * 3. Anti-cheat thresholds (L-2) — super_admin or hr
 * ==================================================================== */

interface AntiCheatConfigResponse {
  config: HiringAntiCheatConfigInput;
}

const ANTI_CHEAT_DEFAULTS: HiringAntiCheatConfigInput = {
  flagAtBlurCount: 5,
  voidAtBlurCount: 10,
  emailHrOnFlag: true,
};

function AntiCheatSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['assessments', 'anti-cheat-config'] as const,
    queryFn: ({ signal }) =>
      apiRequest<AntiCheatConfigResponse>(
        '/api/assessments/settings/anti-cheat',
        { signal },
      ),
  });

  return (
    <SectionShell
      title="Aptitude anti-cheat thresholds"
      description="For hiring aptitude tests, the system records every time a candidate switches tabs or windows. Set how many switches flag an attempt for review, and how many automatically end it."
    >
      <GatedSection
        error={query.error}
        title="Anti-cheat thresholds"
        onRetry={() => query.refetch()}
      >
        {query.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <AntiCheatEditor
            initial={query.data?.config ?? ANTI_CHEAT_DEFAULTS}
            onSaved={() => {
              toast('Anti-cheat thresholds updated.', { variant: 'success' });
              void queryClient.invalidateQueries({
                queryKey: ['assessments', 'anti-cheat-config'],
              });
            }}
          />
        )}
      </GatedSection>
    </SectionShell>
  );
}

function AntiCheatEditor({
  initial,
  onSaved,
}: {
  initial: HiringAntiCheatConfigInput;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<HiringAntiCheatConfigInput>({
    resolver: zodResolver(hiringAntiCheatConfigSchema),
    defaultValues: initial,
  });

  const mutation = useMutation({
    mutationFn: (config: HiringAntiCheatConfigInput) =>
      apiRequest<AntiCheatConfigResponse>(
        '/api/assessments/settings/anti-cheat',
        { method: 'PUT', body: config },
      ),
    onSuccess: () => onSaved(),
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not save.', {
        variant: 'error',
      }),
  });

  return (
    <form
      onSubmit={handleSubmit((v) => mutation.mutate(v))}
      className="max-w-xl space-y-5"
    >
      <TextField
        label="Flag for review after (switches)"
        type="number"
        inputMode="numeric"
        min={1}
        max={50}
        hint="At this many tab/window switches the attempt is flagged for HR review. Default: 5."
        error={errors.flagAtBlurCount?.message}
        {...register('flagAtBlurCount', { valueAsNumber: true })}
      />
      <TextField
        label="End attempt automatically after (switches)"
        type="number"
        inputMode="numeric"
        min={2}
        max={100}
        hint="At this many switches the attempt is ended automatically. Must be greater than the flag threshold. Default: 10."
        error={errors.voidAtBlurCount?.message}
        {...register('voidAtBlurCount', { valueAsNumber: true })}
      />

      <label className="flex items-start gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-[color:var(--color-border)] text-gold-600 focus:ring-gold-500"
          {...register('emailHrOnFlag')}
        />
        <span className="text-sm">
          <span className="font-medium">Email HR when an attempt is flagged</span>
          <span className="mt-0.5 block text-[color:var(--color-text-muted)]">
            Sends a one-time alert to the hiring inbox the first time the flag
            threshold is crossed.
          </span>
        </span>
      </label>

      <div className="flex justify-end">
        <Button type="submit" loading={isSubmitting || mutation.isPending}>
          Save anti-cheat thresholds
        </Button>
      </div>
    </form>
  );
}
